import {
  type SurveyAnswerInput,
  type SurveyPublicQuestion,
} from '../../api/survey'
import { truncateTextAnswer } from './answers'

type Props = {
  question: SurveyPublicQuestion
  answer?: SurveyAnswerInput
  disabled: boolean
  onChange: (answer: SurveyAnswerInput) => void
  questionNumber?: number
}

const choiceInputClassName =
  'h-5 w-5 shrink-0 border-border text-accent focus:ring-accent/40'

const textFieldClassName =
  'w-full rounded-xl border border-border/80 bg-white px-3 py-2.5 text-base text-ink shadow-sm transition placeholder:text-muted/70 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30 disabled:opacity-60'

export default function QuestionField({
  question,
  answer,
  disabled,
  onChange,
  questionNumber,
}: Props) {
  const selectedChoiceIds = answer?.choiceIds ?? []
  const canEdit = !question.answered && !disabled
  const maxSelectionsReached =
    question.type === 'MULTI' &&
    typeof question.maxSelections === 'number' &&
    question.maxSelections > 0 &&
    selectedChoiceIds.length >= question.maxSelections
  const questionTitleId = `survey-question-${question.id}`

  const updateChoice = (choiceId: number) => {
    if (!canEdit) return
    if (question.type === 'SINGLE') {
      onChange({ questionId: question.id, choiceIds: [choiceId] })
      return
    }

    const currentMaxReached =
      typeof question.maxSelections === 'number' &&
      question.maxSelections > 0 &&
      selectedChoiceIds.length >= question.maxSelections
    const nextChoiceIds = selectedChoiceIds.includes(choiceId)
      ? selectedChoiceIds.filter((id) => id !== choiceId)
      : currentMaxReached
        ? selectedChoiceIds
        : [...selectedChoiceIds, choiceId]
    onChange({ questionId: question.id, choiceIds: nextChoiceIds })
  }

  const clearSingleChoice = () => {
    if (!canEdit || question.type !== 'SINGLE' || question.required) return
    onChange({ questionId: question.id, choiceIds: [] })
  }

  const choiceLabelClassName = (checked: boolean, choiceDisabled: boolean) =>
    [
      'flex items-center gap-3 rounded-xl border-2 px-4 py-3 transition-[border-color,background-color]',
      checked
        ? 'border-accent bg-accent-soft'
        : 'border-border/70 bg-white',
      choiceDisabled ? 'opacity-60' : 'cursor-pointer active:bg-accent-soft/60',
    ].join(' ')

  const textValue = answer?.textValue ?? ''

  return (
    <fieldset
      className={`space-y-3 rounded-2xl border border-border/70 bg-white p-4 shadow-sm ${
        question.answered ? 'opacity-80' : ''
      }`}
      disabled={!canEdit}
    >
      <legend className="sr-only">
        {question.title}
        {question.required ? '（必須）' : ''}
      </legend>
      {question.imageUrl ? (
        <img
          src={question.imageUrl}
          alt=""
          className={`w-full rounded object-cover ${
            question.imageAspectRatio === '1:1'
              ? 'aspect-square'
              : 'aspect-video'
          }`}
        />
      ) : null}
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold text-ink" id={questionTitleId} aria-hidden="true">
          {typeof questionNumber === 'number' ? (
            <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-accent align-middle text-xs font-bold text-white">
              {questionNumber}
            </span>
          ) : null}
          {question.title}
        </p>
        <span className="flex shrink-0 items-center gap-1.5">
          {question.answered ? (
            <span className="rounded-full bg-[#F1F5F9] px-2.5 py-0.5 text-xs font-semibold text-[#334155]">
              回答済み
            </span>
          ) : question.required ? (
            <span className="rounded-full bg-danger-soft px-2.5 py-0.5 text-xs font-semibold text-danger">
              必須
            </span>
          ) : (
            <span className="rounded-full bg-[#F1F5F9] px-2.5 py-0.5 text-xs font-semibold text-[#334155]">
              任意
            </span>
          )}
        </span>
      </div>
      {question.type === 'MULTI' && question.maxSelections ? (
        <p className="text-sm text-muted">
          最大{question.maxSelections}個まで選択できます
        </p>
      ) : null}
      {question.type === 'SINGLE' || question.type === 'MULTI' ? (
        <div className="space-y-2">
          {question.choices?.map((choice) => {
            const checked = selectedChoiceIds.includes(choice.id)
            const choiceDisabled =
              !canEdit ||
              (question.type === 'MULTI' && !checked && maxSelectionsReached)
            return (
              <label
                key={choice.id}
                className={choiceLabelClassName(checked, choiceDisabled)}
              >
                <input
                  type={question.type === 'SINGLE' ? 'radio' : 'checkbox'}
                  className={`${choiceInputClassName} ${
                    question.type === 'SINGLE' ? '' : 'rounded'
                  }`}
                  name={`q-${question.id}`}
                  checked={checked}
                  disabled={choiceDisabled}
                  onChange={() => updateChoice(choice.id)}
                />
                <span className="text-base text-ink">{choice.label}</span>
              </label>
            )
          })}
          {question.type === 'SINGLE' && !question.required ? (
            <label
              className={choiceLabelClassName(
                selectedChoiceIds.length === 0,
                !canEdit,
              )}
            >
              <input
                type="radio"
                className={choiceInputClassName}
                name={`q-${question.id}`}
                checked={selectedChoiceIds.length === 0}
                disabled={!canEdit}
                onChange={clearSingleChoice}
              />
              <span className="text-base text-muted">選択しない</span>
            </label>
          ) : null}
        </div>
      ) : null}
      {question.type === 'TEXT' ? (
        <div className="space-y-1">
          <textarea
            aria-labelledby={questionTitleId}
            className={textFieldClassName}
            rows={4}
            placeholder="こちらにご記入ください"
            value={textValue}
            onChange={(event) => {
              if (!canEdit) return
              onChange({
                questionId: question.id,
                textValue: truncateTextAnswer(event.target.value),
              })
            }}
          />
        </div>
      ) : null}
      {question.type === 'DATE' ? (
        <input
          aria-labelledby={questionTitleId}
          type="date"
          className={textFieldClassName}
          value={answer?.dateValue ?? ''}
          onChange={(event) => {
            if (!canEdit) return
            onChange({
              questionId: question.id,
              dateValue: event.target.value,
            })
          }}
        />
      ) : null}
    </fieldset>
  )
}
